import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import { updateProfile } from "api/graphQL_users";
import DivLoader from "components/divloader/DivLoader";
import InputField from "components/fields/InputField";
import React from "react";
import { toast } from "react-hot-toast";
import { MdHighlightOff } from "react-icons/md";
import { PROF_STYLES } from "./ChangePasswordModal";

const EditProfileModal = ({
  data, fetchProfile,
}: {
  data: any; fetchProfile?: () => void;
}) => {
  const rootForm = { first_name: "", last_name: "", email: "", role: "ADMIN" };
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = React.useState<boolean>();
  const [formValues, setFormValues] = React.useState<any>(rootForm);
  const [formValuesErr, setFormValuesErr] = React.useState<any>({});
  const [isCloseModal, setIsCloseModal] = React.useState(false);

  const handleClose = () => {
    setIsCloseModal(true);
    setFormValues(rootForm); setFormValuesErr({});
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  function handleValueChange(e: any) {
    const { id, value } = e.target;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    setFormValues((p: any) => ({ ...p, [id]: value }));
    if (id === "email" && value && !emailRegex.test(value)) {
      setFormValuesErr((p: any) => ({ ...p, email: "Invalid email address" }));
    } else {
      setFormValuesErr((p: any) => ({ ...p, [id]: "" }));
    }
  }

  function validateData() {
    let errors: any = {};
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formValues.first_name) errors.first_name = "Please enter a value";
    if (!formValues.last_name)  errors.last_name  = "Please enter a value";
    if (!formValues.email)      errors.email      = "Please enter a value";
    else if (!emailRegex.test(formValues.email)) errors.email = "Invalid email address";
    setFormValuesErr(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: any) {
    e.preventDefault();
    if (!validateData()) return;
    try {
      setIsLoading(true);
      const create = await updateProfile(formValues);
      if (create.errors) toast.error(create.errors[0].message);
      else if (create.status === "success") {
        fetchProfile?.();
        handleClose();
        toast.success(create?.message ?? "Profile updated successfully");
      } else {
        toast.error(create.message ?? "Something went wrong");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Unable to update profile");
    } finally {
      setIsLoading(false);
    }
  }

  const initForm = () => {
    if (data) {
      setFormValues({ first_name: data.first_name, last_name: data.last_name, email: data.email, role: data.role, password: data.password });
    }
  };

  return (
    <>
      <button className="prof-action-btn" onClick={() => { initForm(); onOpen(); }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
        Edit Profile
      </button>

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
        <div className={`prof-panel${isOpen && !isCloseModal ? " prof-panel--open" : " prof-panel--close"}`}>
          <div className="prof-glow prof-glow--blue" />

          <div className="prof-header prof-header--blue">
            <div className="prof-header-brand">
              <div className="prof-header-logo">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div>
                <h2 className="prof-title">Edit Profile</h2>
                <p className="prof-sub">Update your personal information</p>
              </div>
            </div>
            <button className="prof-close" onClick={handleClose}><MdHighlightOff size={22} /></button>
          </div>

          <div className="prof-body">
            <div className="prof-field-group">
              <InputField variant="auth" extra="w-full" label="First Name *" placeholder="John" id="first_name" type="text"
                value={formValues?.first_name} errMessage={formValuesErr?.first_name} onChange={handleValueChange} />
            </div>
            <div className="prof-field-group">
              <InputField variant="auth" extra="w-full" label="Last Name *" placeholder="Doe" id="last_name" type="text"
                value={formValues?.last_name} errMessage={formValuesErr?.last_name} onChange={handleValueChange} />
            </div>
            <div className="prof-field-group">
              <InputField variant="auth" extra="w-full" label="Email *" placeholder="you@example.com" id="email" type="text"
                value={formValues?.email} errMessage={formValuesErr?.email} onChange={handleValueChange} />
            </div>
          </div>

          <div className="prof-footer">
            <button className="prof-btn-cancel" onClick={handleClose}>Cancel</button>
            <button className="prof-btn-submit--blue" onClick={handleSubmit} disabled={!!isLoading}>
              {isLoading ? <span className="prof-spinner" /> : (
                <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Save Changes</>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {PROF_STYLES}
    </>
  );
};

export default EditProfileModal;